import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { useLocation } from "react-router-dom";
import {
  Upload,
  FileSpreadsheet,
  MapPin,
  Eye,
  CreditCard,
  Download,
  CheckCircle2,
  XCircle,
  Building2,
  TrendingUp,
  Clock,
  Shield,
  ArrowRight,
  Sparkles,
  ChevronRight,
  Star,
  X,
  Table2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/components/ui/sonner";
import {
  staggerContainer,
  fadeInUp,
  scaleIn,
  floatingAnimation,
  floatingTransition,
  floatingAnimationAlt,
  floatingTransitionAlt,
  viewportOptions,
  dragActive,
  dragInactive,
  dragTransition,
} from "@/lib/animations";
import AddressFields from "./AddressFields";
import UserInfoForm from "./UserInfoForm";
import Checkout from "./Checkout";
import VSLHeroSection from "./VSLHeroSection";
import { useDataParser } from "../hooks/useDataParser";
import useFileUploader from "../hooks/useFileUploader";
import { trackEvent } from "../lib/analytics";

// ============================================================================
// DESIGN CONCEPT: "Premium Data Intelligence"
// A sophisticated, trust-building design for real estate professionals
// ============================================================================

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = ({ onScrollToUpload }) => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-amber-50/30 to-slate-100" />

      {/* Geometric Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 right-20 w-64 h-64 rounded-full bg-gradient-to-br from-amber-200/20 to-orange-200/20 blur-3xl"
        animate={floatingAnimation}
        transition={floatingTransition}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-gradient-to-br from-slate-200/30 to-blue-200/20 blur-3xl"
        animate={floatingAnimationAlt}
        transition={floatingTransitionAlt}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/5 border border-slate-200 text-slate-700 text-sm font-medium">
              <Sparkles className="w-4 h-4 text-amber-600" />
              Trusted by 2,500+ Real Estate Professionals
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeInUp}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6"
          >
            Property Data,{" "}
            <span className="relative">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600">
                Enriched
              </span>
              <motion.span
                className="absolute bottom-2 left-0 right-0 h-3 bg-amber-200/50 -z-0"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ originX: 0 }}
              />
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeInUp}
            className="text-xl md:text-2xl text-slate-600 max-w-2xl leading-relaxed mb-8"
          >
            Upload your address list, get comprehensive Zillow property details back.
            No subscription. Pay only for what you need.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-12">
            <Button
              onClick={onScrollToUpload}
              className="group h-14 px-8 text-lg font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300"
            >
              Start Uploading
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="outline"
              className="h-14 px-8 text-lg font-semibold border-2 border-slate-200 hover:border-slate-300 bg-white/50 hover:bg-white text-slate-700 rounded-xl transition-all duration-300"
            >
              See Pricing
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            variants={fadeInUp}
            className="flex flex-wrap items-center gap-8 text-slate-500 text-sm"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              <span>Bank-level encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span>Results in minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span>99.2% data accuracy</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// STATS BANNER
// ============================================================================
const StatsBanner = () => {
  const stats = [
    { value: "2.5M+", label: "Properties Processed", icon: Building2 },
    { value: "15,000+", label: "Hours Saved", icon: Clock },
    { value: "$0.04", label: "Per Property", icon: TrendingUp },
    { value: "100%", label: "No Subscription", icon: Shield },
  ];

  return (
    <section className="relative py-16 bg-slate-900 overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500/10 mb-4">
                <stat.icon className="w-6 h-6 text-amber-500" />
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// PREMIUM UPLOAD SECTION
// ============================================================================
const PremiumUploadSection = React.forwardRef(({ file, onFileChange, errorMessage, isLoading }, ref) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    setIsDragActive(false);
    onFileChange(null);

    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      const errorCode = rejection.errors[0].code;
      let errorMsg = "";
      switch (errorCode) {
        case "file-invalid-type":
          errorMsg = "Invalid file type - Only CSV, XLSX, or XLS files are accepted";
          break;
        case "file-too-large":
          errorMsg = "File too large - Maximum size 200MB";
          break;
        default:
          errorMsg = "Invalid file - Please check format and try again";
      }
      onFileChange({ error: errorMsg });
      return;
    }

    const selectedFile = acceptedFiles[0];
    const validExtensions = [".csv", ".xlsx", ".xls"];
    const isValid = validExtensions.some((ext) =>
      selectedFile.name.toLowerCase().endsWith(ext)
    );

    if (!isValid) {
      onFileChange({
        error: `Invalid file type - Only CSV/XLSX files allowed`,
      });
      return;
    }

    onFileChange(selectedFile);
  }, [onFileChange]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    maxSize: 200 * 1024 * 1024,
    noClick: true,
  });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-slate-100 to-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4"
          >
            Step 1 of 3
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4"
          >
            Upload Your Property List
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 max-w-2xl mx-auto">
            Drop your CSV or Excel file with property addresses. We'll handle the rest.
          </motion.p>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.immediate}
          variants={scaleIn}
        >
          <div
            {...getRootProps()}
            className={`
              relative group cursor-pointer
              rounded-2xl border-2 border-dashed
              transition-all duration-500 ease-out
              ${errorMessage
                ? "border-red-400 bg-red-50/50"
                : file
                  ? "border-emerald-400 bg-emerald-50/50"
                  : isDragActive
                    ? "border-amber-400 bg-amber-50/50 scale-[1.02]"
                    : "border-slate-300 bg-white hover:border-amber-400 hover:bg-amber-50/30"
              }
            `}
          >
            {/* Animated background pattern when dragging */}
            <AnimatePresence>
              {isDragActive && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 rounded-2xl overflow-hidden"
                >
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 10px,
                        #f59e0b 10px,
                        #f59e0b 20px
                      )`,
                      animation: "slide 1s linear infinite",
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative z-10 p-12 md:p-16 text-center">
              {/* Icon */}
              <motion.div
                animate={isDragActive ? dragActive : dragInactive}
                transition={dragTransition}
                className={`
                  inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6
                  ${errorMessage
                    ? "bg-red-100"
                    : file
                      ? "bg-emerald-100"
                      : "bg-slate-100 group-hover:bg-amber-100"
                  }
                  transition-colors duration-300
                `}
              >
                {errorMessage ? (
                  <XCircle className="w-10 h-10 text-red-500" />
                ) : file ? (
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                ) : (
                  <Upload className={`w-10 h-10 ${isDragActive ? "text-amber-600" : "text-slate-400 group-hover:text-amber-600"} transition-colors duration-300`} />
                )}
              </motion.div>

              {/* Text */}
              <input {...getInputProps()} className="hidden" ref={fileInputRef} />

              {file ? (
                <div>
                  <h3 className="text-xl font-semibold text-emerald-700 mb-2">
                    File Ready!
                  </h3>
                  <p className="text-emerald-600 font-medium">{file.name}</p>
                  <p className="text-sm text-emerald-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onFileChange(null);
                    }}
                    className="mt-4 text-sm text-slate-500 hover:text-slate-700 underline"
                  >
                    Choose different file
                  </button>
                </div>
              ) : errorMessage ? (
                <div>
                  <h3 className="text-xl font-semibold text-red-700 mb-2">
                    Upload Error
                  </h3>
                  <p className="text-red-600">{errorMessage}</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onFileChange(null);
                    }}
                    className="mt-4 text-sm text-slate-500 hover:text-slate-700 underline"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    {isDragActive ? "Drop it here!" : "Drag & drop your file"}
                  </h3>
                  <p className="text-slate-500 mb-4">
                    or{" "}
                    <label className="text-amber-600 hover:text-amber-700 font-medium cursor-pointer underline underline-offset-2">
                      browse to upload
                      <input
                        type="file"
                        className="hidden"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0];
                          if (selectedFile) onFileChange(selectedFile);
                        }}
                      />
                    </label>
                  </p>
                  <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
                    <span className="flex items-center gap-1.5">
                      <FileSpreadsheet className="w-4 h-4" />
                      CSV
                    </span>
                    <span className="flex items-center gap-1.5">
                      <FileSpreadsheet className="w-4 h-4" />
                      XLSX
                    </span>
                    <span>Max 200MB</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
});

PremiumUploadSection.displayName = "PremiumUploadSection";

// ============================================================================
// HOW IT WORKS SECTION
// ============================================================================
const HowItWorksSection = () => {
  const steps = [
    {
      icon: Upload,
      title: "Upload Your File",
      description: "Drop your CSV or Excel file with property addresses. We support any format with street, city, state, and ZIP.",
      color: "amber",
    },
    {
      icon: MapPin,
      title: "Map Your Columns",
      description: "Our smart detection finds your address columns automatically. Just confirm and adjust if needed.",
      color: "blue",
    },
    {
      icon: Eye,
      title: "Preview & Verify",
      description: "See a sample of your addresses formatted correctly before proceeding. Catch errors early.",
      color: "purple",
    },
    {
      icon: CreditCard,
      title: "Pay Once",
      description: "Simple per-row pricing. No subscription, no hidden fees. Pay only for what you process.",
      color: "emerald",
    },
    {
      icon: Download,
      title: "Get Your Data",
      description: "Receive your enriched file with complete Zillow property details via email within minutes.",
      color: "rose",
    },
  ];

  const colorClasses = {
    amber: { bg: "bg-amber-100", text: "text-amber-600", border: "border-amber-200" },
    blue: { bg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
    purple: { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
    emerald: { bg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200" },
    rose: { bg: "bg-rose-100", text: "text-rose-600", border: "border-rose-200" },
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm font-medium mb-4"
          >
            Simple Process
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-5xl font-bold text-slate-900 mb-4"
          >
            How It Works
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get from raw addresses to enriched property data in five simple steps
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.shallow}
          variants={staggerContainer}
          className="relative"
        >
          {/* Connection line - positioned at vertical center of icons (icons are h-16 = 64px, center at 32px = top-8) */}
          <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-amber-200 via-blue-200 via-purple-200 via-emerald-200 to-rose-200 -z-10" />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => {
              const colors = colorClasses[step.color];
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="relative text-center"
                >
                  {/* Step number */}
                  <div className={`relative z-10 inline-flex items-center justify-center w-16 h-16 rounded-2xl ${colors.bg} ${colors.border} border-2 mb-6 shadow-sm`}>
                    <step.icon className={`w-7 h-7 ${colors.text}`} />
                  </div>

                  {/* Arrow for desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 -right-4 z-20">
                      <ChevronRight className="w-6 h-6 text-slate-300" />
                    </div>
                  )}

                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// TESTIMONIALS SECTION
// ============================================================================
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "This tool saved us 20+ hours on our last property analysis project. The data accuracy is incredible.",
      author: "Sarah Chen",
      role: "Real Estate Investor",
      company: "Apex Properties",
      rating: 5,
    },
    {
      quote: "Finally, a bulk property lookup that just works. No subscriptions, no complexity. Upload and done.",
      author: "Marcus Williams",
      role: "Portfolio Manager",
      company: "Residential Capital",
      rating: 5,
    },
    {
      quote: "We've processed over 50,000 properties through this. It's become essential to our research workflow.",
      author: "Jennifer Park",
      role: "Market Analyst",
      company: "DataDriven RE",
      rating: 5,
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4"
          >
            Trusted by Professionals
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-5xl font-bold text-slate-900 mb-4"
          >
            What Our Users Say
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.shallow}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="relative bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all duration-300"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 font-semibold">
                  {testimonial.author.split(" ").map(n => n[0]).join("")}
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{testimonial.author}</div>
                  <div className="text-sm text-slate-500">{testimonial.role} at {testimonial.company}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
const CTASection = ({ onScrollToUpload }) => {
  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-amber-500 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-blue-500 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.immediate}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-5xl font-bold text-white mb-6"
          >
            Ready to Enrich Your Property Data?
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto"
          >
            Join thousands of real estate professionals who save hours every week with our bulk property lookup tool.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Button
              onClick={onScrollToUpload}
              className="group h-16 px-12 text-xl font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 transition-all duration-300"
            >
              Get Started Now
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
          <motion.p
            variants={fadeInUp}
            className="mt-6 text-sm text-slate-400"
          >
            No account needed — upload your file and proceed instantly
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// FOOTER
// ============================================================================
const Footer = () => {
  return (
    <footer className="py-12 bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-white">
              PropertyBulk
            </span>
          </div>

          <div className="flex items-center gap-8 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/contact" className="hover:text-white transition-colors">Contact</a>
          </div>

          <div className="text-sm text-slate-500">
            © 2024 PropertyBulk. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// COLUMN MAPPING SECTION
// ============================================================================
const ColumnMappingSection = ({ address, setAddress, data, onUserChangedAddressColumns }) => {
  return (
    <section className="py-12 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-4"
          >
            Step 2 of 3
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl font-bold text-slate-900 mb-4"
          >
            Map Your Columns
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-8">
            Confirm which columns contain your address data.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <AddressFields
              address={address}
              setAddress={setAddress}
              data={data}
              onUserChangedAddressColumns={onUserChangedAddressColumns}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// DATA PREVIEW SECTION
// ============================================================================
const DataPreviewSection = ({ data, address }) => {
  // Check if all address fields are selected
  const allFieldsSelected =
    address.street.value &&
    address.city.value &&
    address.state.value &&
    address.zip.value;

  if (!allFieldsSelected || data.length < 2) return null;

  // Get column indices
  const indices = {
    street: data[0].indexOf(address.street.value),
    city: data[0].indexOf(address.city.value),
    state: data[0].indexOf(address.state.value),
    zip: data[0].indexOf(address.zip.value),
  };

  // Get preview rows (first 5 data rows, excluding header)
  const previewRows = data.slice(1, 6);
  const totalDataRows = data.length - 1;
  const showingCount = Math.min(5, totalDataRows);

  // Column configuration for display
  const columns = [
    { key: "street", label: address.street.value, icon: "🏠" },
    { key: "city", label: address.city.value, icon: "🏙️" },
    { key: "state", label: address.state.value, icon: "📍" },
    { key: "zip", label: address.zip.value, icon: "✉️" },
  ];

  return (
    <section className="py-8 bg-gradient-to-b from-white to-slate-50/50">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="flex items-center justify-between mb-6"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-100 border border-purple-200">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-900">
                  Preview Selected Addresses
                </h3>
                <p className="text-sm text-slate-500">
                  Verify your column mapping is correct
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
              <Table2 className="w-3.5 h-3.5" />
              Showing {showingCount} of {totalDataRows.toLocaleString()} rows
            </span>
          </motion.div>

          {/* Preview Table */}
          <motion.div
            variants={fadeInUp}
            className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            {/* Decorative gradient line at top */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 via-purple-400 to-blue-400" />

            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Frosted Glass Header */}
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-slate-100/80 backdrop-blur-sm border-b border-slate-200">
                    {columns.map((col, idx) => (
                      <th
                        key={col.key}
                        className={`
                          px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600
                          ${idx === 0 ? "pl-5" : ""}
                          ${idx === columns.length - 1 ? "pr-5" : ""}
                        `}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{col.icon}</span>
                          <span className="truncate max-w-[120px]" title={col.label}>
                            {col.label}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Table Body with Alternating Rows */}
                <tbody className="divide-y divide-slate-100">
                  {previewRows.map((row, rowIdx) => (
                    <motion.tr
                      key={rowIdx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + rowIdx * 0.05, duration: 0.3 }}
                      className={`
                        group transition-colors duration-150
                        ${rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                        hover:bg-amber-50/50
                      `}
                    >
                      {columns.map((col, colIdx) => (
                        <td
                          key={col.key}
                          className={`
                            px-4 py-3 text-sm text-slate-700
                            ${colIdx === 0 ? "pl-5 font-medium" : ""}
                            ${colIdx === columns.length - 1 ? "pr-5" : ""}
                            ${col.key === "state" ? "uppercase" : ""}
                          `}
                        >
                          <span className="truncate block max-w-[200px]" title={row[indices[col.key]] || "—"}>
                            {row[indices[col.key]] || (
                              <span className="text-slate-300 italic">empty</span>
                            )}
                          </span>
                        </td>
                      ))}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom fade indicator if there are more rows */}
            {totalDataRows > 5 && (
              <div className="relative h-8 bg-gradient-to-t from-white via-white/80 to-transparent -mt-8 pointer-events-none" />
            )}
          </motion.div>

          {/* Success indicator */}
          <motion.div
            variants={fadeInUp}
            className="mt-4 flex items-center gap-2 text-sm text-emerald-600"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>All address columns mapped correctly</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// PRICING SECTION
// ============================================================================
const PricingSection = ({ count, totalPrice }) => (
  <section className="py-12 bg-slate-50">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          variants={fadeInUp}
          className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm"
        >
          <h3 className="font-display text-xl font-semibold text-slate-900 mb-6">
            Order Summary
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">Zillow Property Details</span>
              <span className="text-slate-900 font-medium">
                {count.toLocaleString()} rows
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">Price per row</span>
              <span className="text-slate-900 font-medium">$0.04</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">Base fee</span>
              <span className="text-slate-900 font-medium">$5.00</span>
            </div>
            <div className="flex justify-between items-center pt-4">
              <span className="text-lg font-semibold text-slate-900">Total</span>
              <span className="font-display text-2xl font-bold text-slate-900">
                ${totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
          {count > 100000 && (
            <div className="mt-6 bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-lg text-sm">
              Note: For requests over 100,000 rows, please contact our support team for enterprise pricing.
            </div>
          )}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ============================================================================
// CHECKOUT SECTION
// ============================================================================
const CheckoutSection = ({
  userInfo,
  setUserInfo,
  formError,
  uploadError,
  isPurchaseDisabled,
  data,
  address,
  setUploadedFileMeta,
  totalPrice,
  sessionToken,
}) => (
  <section className="py-12 bg-white">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.span
          variants={fadeInUp}
          className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4"
        >
          Step 3 of 3
        </motion.span>
        <motion.h2
          variants={fadeInUp}
          className="font-display text-3xl font-bold text-slate-900 mb-4"
        >
          Complete Your Order
        </motion.h2>

        <motion.div variants={fadeInUp}>
          <UserInfoForm
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            errorMessage={formError}
            uploadError={uploadError}
          />
        </motion.div>

        <motion.div variants={fadeInUp} className="mt-8">
          <Checkout
            isPurchaseDisabled={isPurchaseDisabled}
            data={data}
            address={address}
            userInfo={userInfo}
            setUploadedFileMeta={setUploadedFileMeta}
            totalPrice={totalPrice}
            skipTracingService={false}
            zillowService={true}
            sessionToken={sessionToken}
          />
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ============================================================================
// MAIN HOMEPAGE COMPONENT
// ============================================================================
export default function HomepageRedesign() {
  const uploadRef = useRef(null);
  const previousFileRef = useRef(null);
  const sessionWarningShownRef = useRef(null);
  const location = useLocation();

  // Session state
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [uploadedFileMeta, setUploadedFileMeta] = useState(null);

  // Address state
  const [address, setAddress] = useState({
    street: { index: null, value: "" },
    city: { index: null, value: "" },
    state: { index: null, value: "" },
    zip: { index: null, value: "" },
  });

  // User info state
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  // Form state
  const [hasUserSelectedColumns, setHasUserSelectedColumns] = useState(false);
  const [isPurchaseDisabled, setIsPurchaseDisabled] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const [formError, setFormError] = useState("");
  const [autoDetectedCount, setAutoDetectedCount] = useState(0);
  const [pendingToast, setPendingToast] = useState(false);

  // Data hooks
  const {
    isLoading,
    file,
    data,
    count,
    errorMessage: fileError,
    parseFile,
    setErrorMessage: setFileError,
    resetParser,
  } = useDataParser();

  const {
    uploadToS3,
    uploadError,
    uploadProgress,
    isUploading,
    uploadStats,
    abortUpload: abortFileUpload,
  } = useFileUploader(sessionToken);

  // Required fields for validation
  const requiredFields = ["street", "city", "state", "zip"];

  // Auto-detect address columns
  const autoDetectAddressColumns = useCallback((headers) => {
    const detected = { street: null, city: null, state: null, zip: null };

    detected.street = headers.find((h) =>
      ["address", "street", "addr", "location"].some((k) =>
        h.toLowerCase().includes(k)
      )
    );

    detected.city = headers.find((h) =>
      ["city", "town"].some((k) => h.toLowerCase().includes(k))
    );

    detected.state = headers.find((h) =>
      ["state", "st."].some((k) => h.toLowerCase().includes(k))
    );

    detected.zip = headers.find((h) =>
      ["zip", "zipcode", "postal", "postcode"].some((k) =>
        h.toLowerCase().includes(k)
      )
    );

    return detected;
  }, []);

  // Check if all address fields are selected
  const allAddressFieldsSelected = useMemo(
    () => requiredFields.every((field) => address[field].value),
    [address]
  );

  // Auto-detect address columns when data loads
  useEffect(() => {
    if (data.length > 0 && data[0] && !hasUserSelectedColumns) {
      const detected = autoDetectAddressColumns(data[0]);
      const detectedCount = Object.values(detected).filter(Boolean).length;
      setAutoDetectedCount(detectedCount);

      setAddress((prev) => ({
        street: detected.street
          ? { value: detected.street, index: data[0].indexOf(detected.street) }
          : prev.street,
        city: detected.city
          ? { value: detected.city, index: data[0].indexOf(detected.city) }
          : prev.city,
        state: detected.state
          ? { value: detected.state, index: data[0].indexOf(detected.state) }
          : prev.state,
        zip: detected.zip
          ? { value: detected.zip, index: data[0].indexOf(detected.zip) }
          : prev.zip,
      }));

      if (detectedCount > 0) {
        setPendingToast(true);
      }
    }
  }, [data, autoDetectAddressColumns, hasUserSelectedColumns]);

  // Show toast after loading completes
  useEffect(() => {
    if (!file || data.length === 0) return;
    if (!isLoading && pendingToast && autoDetectedCount > 0) {
      toast.success(`Auto-detected ${autoDetectedCount}/4 address columns`);
      setPendingToast(false);
    }
  }, [isLoading, pendingToast, autoDetectedCount, file, data.length]);

  // Track pageview
  useEffect(() => {
    trackEvent("page.view", { page: "homepage-redesign" });
  }, [location]);

  // Initialize session
  useEffect(() => {
    if (sessionToken) return;

    const initSession = async () => {
      try {
        const isDev =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        const enableRealCheckout =
          import.meta.env.VITE_ENABLE_REAL_CHECKOUT === "true";

        let sessionData;

        if (isDev && !enableRealCheckout && !window.location.href.includes("vercel.app")) {
          const testDuration = import.meta.env.VITE_TEST_SESSION_DURATION;
          const sessionDurationSeconds = testDuration ? parseInt(testDuration) : 1800;
          const sessionDurationMs = sessionDurationSeconds * 1000;

          sessionData = {
            sessionToken: "dev-session-" + Math.random().toString(36).substr(2, 9),
            expiresIn: sessionDurationSeconds,
            expiresAt: new Date(Date.now() + sessionDurationMs).toISOString(),
          };
        } else {
          const response = await fetch("/api/create-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });

          if (!response.ok) throw new Error("Failed to create session");
          sessionData = await response.json();
        }

        setSessionToken(sessionData.sessionToken);
        setSessionExpiry(new Date(sessionData.expiresAt));
        trackEvent("security.session.created");
      } catch (error) {
        console.error("Session initialization failed:", error);
        toast.error("Failed to initialize secure session. Please refresh the page.");
        trackEvent("security.session.error", { error: error.message });
      }
    };

    initSession();
  }, [sessionToken]);

  // Check session expiry
  useEffect(() => {
    if (!sessionExpiry) return;

    const testDuration = import.meta.env.VITE_TEST_SESSION_DURATION;
    const isTestMode = testDuration && parseInt(testDuration) < 300;

    const checkExpiry = setInterval(() => {
      if (sessionExpired) {
        clearInterval(checkExpiry);
        return;
      }

      const now = new Date();
      const timeLeft = sessionExpiry - now;

      if (timeLeft <= 0) {
        toast.error("Session expired. Please refresh the page to continue.");
        trackEvent("security.session.expired");
        resetParser();
        setSessionExpired(true);
        clearInterval(checkExpiry);
        return;
      }

      if (!sessionExpired && !sessionWarningShownRef.current) {
        if (isTestMode) {
          if (timeLeft < 10 * 1000 && timeLeft > 0) {
            toast.warning("Your session will expire in 10 seconds!");
            sessionWarningShownRef.current = true;
            trackEvent("security.session.warning", { secondsLeft: 10 });
          }
        } else {
          if (timeLeft < 5 * 60 * 1000) {
            toast.warning("Your session will expire in 5 minutes. Please complete your upload.");
            sessionWarningShownRef.current = true;
            trackEvent("security.session.warning", { minutesLeft: 5 });
          }
        }
      }
    }, 1000);

    return () => clearInterval(checkExpiry);
  }, [sessionExpiry, sessionExpired, resetParser]);

  // Email validation helper
  const isValidEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email?.trim() || "");
  }, []);

  // Name validation helper (at least 2 chars, letters only)
  const isValidName = useCallback((name) => {
    const trimmed = name?.trim() || "";
    return trimmed.length >= 2 && /^[a-zA-Z\s'-]+$/.test(trimmed);
  }, []);

  // Compute total price
  useEffect(() => {
    const basePrice = 5;
    const costPerUnit = 0.04;
    const quantity = count > 0 ? count : 0;

    const zillowCost = quantity * costPerUnit + basePrice;
    setTotalPrice(zillowCost);

    const isCountInvalid = quantity > 100000;
    const areFieldsMissing =
      !allAddressFieldsSelected ||
      !userInfo.firstName ||
      !userInfo.lastName ||
      !userInfo.email;
    const hasInvalidFields =
      !isValidName(userInfo.firstName) ||
      !isValidName(userInfo.lastName) ||
      !isValidEmail(userInfo.email);

    setIsPurchaseDisabled(
      isCountInvalid ||
        areFieldsMissing ||
        hasInvalidFields ||
        isLoading ||
        data.length === 0 ||
        sessionExpired ||
        !sessionToken
    );
  }, [
    count,
    allAddressFieldsSelected,
    userInfo,
    isLoading,
    data,
    sessionExpired,
    sessionToken,
    isValidEmail,
    isValidName,
  ]);

  // Handle file change - integrate with parseFile
  const handleFileChange = useCallback(
    (newFile) => {
      setFormError("");
      setFileError("");

      if (!newFile) {
        resetParser();
        setAddress({
          street: { index: null, value: "" },
          city: { index: null, value: "" },
          state: { index: null, value: "" },
          zip: { index: null, value: "" },
        });
        setHasUserSelectedColumns(false);
        setPendingToast(false);
        setAutoDetectedCount(0);
        previousFileRef.current = null;
        return;
      }

      if (newFile?.error) {
        setFormError(newFile.error);
        setFileError(newFile.error);
        setHasUserSelectedColumns(false);
        return;
      }

      if (newFile && !(newFile instanceof File)) {
        return;
      }

      if (newFile?.name.match(/\.(csv|xlsx|xls)$/i)) {
        if (
          previousFileRef.current &&
          newFile.name === previousFileRef.current.name &&
          newFile.size === previousFileRef.current.size
        ) {
          return;
        }
        parseFile(newFile);
        previousFileRef.current = newFile;
        setAddress({
          street: { index: null, value: "" },
          city: { index: null, value: "" },
          state: { index: null, value: "" },
          zip: { index: null, value: "" },
        });
        setHasUserSelectedColumns(false);
      }
    },
    [parseFile, setFileError, resetParser]
  );

  const scrollToUpload = useCallback(() => {
    uploadRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center space-y-4">
            <Spinner className="w-16 h-16" />
            <p className="text-slate-600 font-medium">Processing your file...</p>
          </div>
        </div>
      )}

      {/* Session expired overlay */}
      {sessionExpired && (
        <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center">
          <Card className="max-w-md mx-4 p-8 text-center space-y-6">
            <div className="space-y-2">
              <h2 className="font-display text-2xl font-bold text-slate-900">
                Session Expired
              </h2>
              <p className="text-slate-600">
                Your secure session has expired for your protection. Please refresh
                the page to start a new session.
              </p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => window.location.reload()}
                className="w-full"
                size="lg"
              >
                Refresh Page
              </Button>
              <p className="text-xs text-slate-500">
                Sessions expire after 30 minutes of inactivity
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Hero - only show when no file */}
      {/* VSL Hero - Video first design */}
      {!file && (
        <>
          <VSLHeroSection onScrollToUpload={scrollToUpload} />
          <StatsBanner />
        </>
      )}

      {/* Upload section - always visible */}
      <PremiumUploadSection
        ref={uploadRef}
        file={file}
        onFileChange={handleFileChange}
        errorMessage={fileError}
        isLoading={isLoading}
      />

      {/* Column mapping - after file selected */}
      {file && data.length > 0 && (
        <ColumnMappingSection
          address={address}
          setAddress={setAddress}
          data={data}
          onUserChangedAddressColumns={() => setHasUserSelectedColumns(true)}
        />
      )}

      {/* Data preview - shows when all columns are mapped */}
      {file && data.length > 0 && (
        <DataPreviewSection data={data} address={address} />
      )}

      {/* Pricing - after file selected */}
      {file && data.length > 0 && (
        <PricingSection count={count} totalPrice={totalPrice} />
      )}

      {/* Checkout - after all fields valid */}
      {file && data.length > 0 && allAddressFieldsSelected && (
        <CheckoutSection
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          formError={formError}
          uploadError={uploadError}
          isPurchaseDisabled={isPurchaseDisabled}
          data={data}
          address={address}
          setUploadedFileMeta={setUploadedFileMeta}
          totalPrice={totalPrice}
          sessionToken={sessionToken}
        />
      )}

      {/* Marketing sections - only when no file */}
      {!file && (
        <>
          <HowItWorksSection />
          <TestimonialsSection />
          <CTASection onScrollToUpload={scrollToUpload} />
        </>
      )}

      <Footer />
    </div>
  );
}
